import { Capacitor } from '@capacitor/core';
import { blobToBase64 } from './blobToBase64';
import {
  ALLOWED_EXTENSIONS,
  assertMimeTypeMatchesFilename,
  getMimeTypeFromFilename,
  MimeType,
} from './fileValidation';
import { CapacitorBridge } from '@/services/CapacitorBridge';

export function createTypedBlob(
  data: BlobPart | ArrayBuffer,
  filename: string | null,
  mimeType: MimeType,
): Blob {
  if (filename !== null) {
    assertMimeTypeMatchesFilename(filename, mimeType);
  }
  return new Blob([data], { type: mimeType });
}

/** Convenience when MIME is derived from filename — still runs validation internally. */
export function createBlobForFile(
  data: BlobPart | ArrayBuffer,
  filename: string,
): Blob {
  const mimeType = getMimeTypeFromFilename(filename);
  return createTypedBlob(data, filename, mimeType);
}

export async function triggerBlobDownload(
  blob: Blob,
  filename: string,
  mimeType: MimeType,
): Promise<void> {
  assertMimeTypeMatchesFilename(filename, mimeType);

  const typedBlob =
    blob.type === mimeType
      ? blob
      : createTypedBlob(await blob.arrayBuffer(), filename, mimeType);

  const safeFileName = filename.replace(/[/\\?%*:|"<>]/g, '-').trim();

  if (Capacitor.getPlatform() === 'web') {
    const blobUrl = URL.createObjectURL(typedBlob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = safeFileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
    return;
  }

  const base64 = await blobToBase64(typedBlob);

  try {
    await CapacitorBridge.execute('requestPermissions');
  } catch (e) {
    console.warn('Permission request failed or rejected', e);
  }

  const { uri } = await CapacitorBridge.execute('writeFile', {
    path: safeFileName,
    data: base64,
    isBinary: true,
  });

  if (Capacitor.getPlatform() === 'ios') {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext && !ALLOWED_EXTENSIONS.includes(ext)) {
      await CapacitorBridge.execute('share', {
        title: filename,
        url: uri,
      });
      return;
    }
  }

  try {
    await CapacitorBridge.execute('openFile', {
      filePath: uri,
    });
  } catch (error: unknown) {
    const err = error as { code?: string };
    if (err.code === 'OS-PLUG-FLVW-0010') {
      const { dispatchFileOpenError } =
        await import('../components/common/FileOpenErrorDialog');
      dispatchFileOpenError(filename, uri);
    } else {
      throw error;
    }
  }
}
